import { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";

/**
 * Beyond Sure — account / profile dropdown.
 *
 * PORTED FROM `assets/form.html:12504-12580` so every item the
 * founder shipped lives in the new clean shell.
 *
 * Anchor: the sidebar account footer button. Click to toggle the
 * floating panel above-right of the avatar.
 *
 * Panel contents (top → bottom):
 *   - Name · email · plan badge (Enterprise gets a gradient)
 *   - "Messages today  X / 150" progress bar (turns red above 80%)
 *   - "↑ Upgrade plan" CTA
 *   - Language selector (13 Indian-language options + English)
 *   - Settings           (⌘,)
 *   - Billing & Plan
 *   - Notifications
 *   - Dark mode toggle (label flips Light/Dark)
 *   - Keyboard shortcuts (⌘/)
 *   - Help & support
 *   - Privacy & Data
 *   - Log out (danger)
 *
 * Brand fidelity: same brand-deep, brand-soft, ink-08 palette as
 * the rest of the new shell; tasteful glass; no regression to the
 * old cramped chrome.
 *
 * // TODO: wire to /api/* once backend exposes:
 *   - GET /api/usage       → messages today + plan name
 *   - PATCH /api/user/lang → language change
 *   - POST /api/logout     → log out
 *   - all "open …" callbacks → real modals
 */

export const LANGUAGES = [
  { code: "english", label: "English" },
  { code: "hindi", label: "हिन्दी (Hindi)" },
  { code: "bengali", label: "বাংলা (Bengali)" },
  { code: "telugu", label: "తెలుగు (Telugu)" },
  { code: "marathi", label: "मराठी (Marathi)" },
  { code: "tamil", label: "தமிழ் (Tamil)" },
  { code: "gujarati", label: "ગુજરાતી (Gujarati)" },
  { code: "kannada", label: "ಕನ್ನಡ (Kannada)" },
  { code: "odia", label: "ଓଡ଼ିଆ (Odia)" },
  { code: "punjabi", label: "ਪੰਜਾਬੀ (Punjabi)" },
  { code: "malayalam", label: "മലയാളം (Malayalam)" },
  { code: "assamese", label: "অসমীয়া (Assamese)" },
  { code: "urdu", label: "اردو (Urdu)" },
] as const;

export type LanguageCode = (typeof LANGUAGES)[number]["code"];

export interface ProfileMenuProps {
  userName?: string;
  userEmail?: string;
  planName?: string;
  isEnterprise?: boolean;
  messagesUsed?: number;
  messagesLimit?: number;
  language?: LanguageCode;
  darkMode?: boolean;
  onLanguageChange?: (code: LanguageCode) => void;
  onUpgrade?: () => void;
  onSettings?: () => void;
  onBilling?: () => void;
  onNotifications?: () => void;
  onToggleDarkMode?: () => void;
  onKeyboardShortcuts?: () => void;
  onHelp?: () => void;
  onPrivacy?: () => void;
  onLogout?: () => void;
  /** Render-prop trigger: receives onClick + ref to attach. */
  children: (props: {
    onClick: () => void;
    ref: React.RefObject<HTMLButtonElement | null>;
    "aria-expanded": boolean;
    "aria-haspopup": "menu";
  }) => React.ReactNode;
}

export function ProfileMenu({
  userName = "You",
  userEmail = "",
  planName = "Free",
  isEnterprise = false,
  messagesUsed = 0,
  messagesLimit = 150,
  language = "english",
  darkMode = false,
  onLanguageChange,
  onUpgrade,
  onSettings,
  onBilling,
  onNotifications,
  onToggleDarkMode,
  onKeyboardShortcuts,
  onHelp,
  onPrivacy,
  onLogout,
  children,
}: ProfileMenuProps) {
  const [open, setOpen] = useState(false);
  const anchorRef = useRef<HTMLButtonElement | null>(null);
  const panelRef = useRef<HTMLDivElement | null>(null);

  // Click-outside to close.
  useEffect(() => {
    if (!open) return;
    function onDocClick(e: MouseEvent) {
      const t = e.target as Node;
      if (
        panelRef.current?.contains(t) ||
        anchorRef.current?.contains(t)
      ) return;
      setOpen(false);
    }
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, [open]);

  // Esc to close.
  useEffect(() => {
    if (!open) return;
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") setOpen(false);
    }
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open]);

  const pct = messagesLimit > 0
    ? Math.min(100, Math.round((messagesUsed / messagesLimit) * 100))
    : 0;
  const usageHot = pct > 80;

  return (
    <>
      {children({
        onClick: () => setOpen((o) => !o),
        ref: anchorRef,
        "aria-expanded": open,
        "aria-haspopup": "menu",
      })}
      <AnimatePresence>
        {open ? (
          <motion.div
            ref={panelRef}
            initial={{ opacity: 0, y: 6 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 6 }}
            transition={{ duration: 0.18 }}
            role="menu"
            data-testid="profile-menu"
            className={[
              "fixed bottom-[64px] left-2.5 z-[70]",
              "w-[268px] max-h-[calc(100dvh-100px)] overflow-y-auto",
              "rounded-[14px] p-2",
              "bg-[var(--color-paper)] border border-[var(--color-ink-08)]",
              "shadow-[0_18px_48px_-18px_rgba(20,75,51,0.35)]",
            ].join(" ")}
          >
            {/* ─── Header — name / email / plan badge ─────────── */}
            <div
              className={[
                "px-2.5 pt-2 pb-3 border-b border-[var(--color-ink-08)]",
              ].join(" ")}
            >
              <p
                data-testid="profile-name"
                className="text-[14px] font-semibold text-[var(--color-ink)] truncate"
              >
                {userName}
              </p>
              {userEmail ? (
                <p
                  data-testid="profile-email"
                  className="text-[11.5px] text-[var(--color-ink-40)] truncate mt-0.5"
                >
                  {userEmail}
                </p>
              ) : null}
              <PlanBadge planName={planName} isEnterprise={isEnterprise} />
            </div>

            {/* ─── Messages-today counter + bar ──────────────── */}
            {messagesLimit > 0 ? (
              <div
                className="px-2.5 py-2 border-b border-[var(--color-ink-08)]"
                data-testid="profile-usage"
              >
                <div className="flex items-center justify-between text-[11px] text-[var(--color-ink-60)]">
                  <span>Messages today</span>
                  <span className="tabular-nums font-semibold text-[var(--color-ink)]">
                    {messagesUsed} / {messagesLimit}
                  </span>
                </div>
                <div
                  className={[
                    "mt-1.5 h-1 rounded-full overflow-hidden",
                    "bg-[var(--color-ink-08)]",
                  ].join(" ")}
                  aria-hidden
                >
                  <div
                    data-testid="profile-usage-bar"
                    className="h-full rounded-full transition-all"
                    style={{
                      width: `${pct}%`,
                      backgroundColor: usageHot
                        ? "#cf6f6f"
                        : "var(--color-brand-deep)",
                    }}
                  />
                </div>
              </div>
            ) : null}

            {/* ─── Upgrade CTA ────────────────────────────────── */}
            <button
              type="button"
              onClick={() => {
                onUpgrade?.();
                setOpen(false);
              }}
              data-testid="profile-upgrade"
              className={[
                "w-full mt-2 mb-1.5 px-3 py-2 rounded-[10px]",
                "text-[12.5px] font-semibold",
                "bg-[var(--color-brand-deep)] text-[var(--color-paper)]",
                "hover:opacity-95 transition-opacity",
                "flex items-center justify-center gap-1.5",
              ].join(" ")}
            >
              ↑ Upgrade plan
            </button>

            {/* ─── Language selector ─────────────────────────── */}
            <div
              className="px-2.5 py-1.5 flex items-center gap-2"
              data-testid="profile-language"
            >
              <span className="bsure-caps text-[10px] tracking-[0.14em] font-semibold text-[var(--color-brand-deep)]">
                Language
              </span>
              <select
                value={language}
                onChange={(e) =>
                  onLanguageChange?.(e.target.value as LanguageCode)
                }
                data-testid="profile-language-select"
                className={[
                  "ml-auto h-7 px-2 rounded-[8px]",
                  "border border-[var(--color-ink-08)] bg-[var(--color-paper)]",
                  "text-[12px] text-[var(--color-ink)]",
                  "focus:outline-none focus:border-[var(--color-brand-deep)]",
                ].join(" ")}
              >
                {LANGUAGES.map((l) => (
                  <option key={l.code} value={l.code}>
                    {l.label}
                  </option>
                ))}
              </select>
            </div>

            {/* ─── Menu items ────────────────────────────────── */}
            <div className="mt-1 flex flex-col">
              <MenuItem
                label="Settings"
                kbd="⌘,"
                testId="profile-settings"
                onClick={() => {
                  onSettings?.();
                  setOpen(false);
                }}
                icon={
                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" aria-hidden>
                    <circle cx="12" cy="12" r="3" stroke="currentColor" strokeWidth="1.8" />
                    <path
                      d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z"
                      stroke="currentColor"
                      strokeWidth="1.8"
                      strokeLinejoin="round"
                    />
                  </svg>
                }
              />
              <MenuItem
                label="Billing & Plan"
                testId="profile-billing"
                onClick={() => {
                  onBilling?.();
                  setOpen(false);
                }}
                icon={
                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" aria-hidden>
                    <rect
                      x="1.5"
                      y="4.5"
                      width="21"
                      height="15"
                      rx="2"
                      stroke="currentColor"
                      strokeWidth="1.8"
                    />
                    <path d="M1.5 10h21" stroke="currentColor" strokeWidth="1.8" />
                  </svg>
                }
              />
              <MenuItem
                label="Notifications"
                testId="profile-notifications"
                onClick={() => {
                  onNotifications?.();
                  setOpen(false);
                }}
                icon={
                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" aria-hidden>
                    <path
                      d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9M13.7 21a2 2 0 0 1-3.4 0"
                      stroke="currentColor"
                      strokeWidth="1.8"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                }
              />
              <MenuItem
                label={darkMode ? "Light mode" : "Dark mode"}
                testId="profile-dark"
                onClick={() => {
                  onToggleDarkMode?.();
                  // keep menu open so the toggle feels immediate
                }}
                icon={
                  <span className="inline-block text-[13px] leading-none w-[13px] text-center">
                    {darkMode ? "☀" : "🌙"}
                  </span>
                }
              />
              <MenuItem
                label="Keyboard shortcuts"
                kbd="⌘/"
                testId="profile-kbd"
                onClick={() => {
                  onKeyboardShortcuts?.();
                  setOpen(false);
                }}
                icon={
                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" aria-hidden>
                    <rect
                      x="2.5"
                      y="3.5"
                      width="19"
                      height="13"
                      rx="2"
                      stroke="currentColor"
                      strokeWidth="1.8"
                    />
                    <path
                      d="M8 21h8M12 17v4"
                      stroke="currentColor"
                      strokeWidth="1.8"
                      strokeLinecap="round"
                    />
                  </svg>
                }
              />
              <div
                className="my-1 mx-1 h-px bg-[var(--color-ink-08)]"
                aria-hidden
              />
              <MenuItem
                label="Help & support"
                testId="profile-help"
                onClick={() => {
                  onHelp?.();
                  setOpen(false);
                }}
                icon={
                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" aria-hidden>
                    <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="1.8" />
                    <path
                      d="M12 8v4M12 16h.01"
                      stroke="currentColor"
                      strokeWidth="1.8"
                      strokeLinecap="round"
                    />
                  </svg>
                }
              />
              <MenuItem
                label="Privacy & Data"
                testId="profile-privacy"
                onClick={() => {
                  onPrivacy?.();
                  setOpen(false);
                }}
                icon={
                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" aria-hidden>
                    <path
                      d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"
                      stroke="currentColor"
                      strokeWidth="1.8"
                      strokeLinejoin="round"
                    />
                  </svg>
                }
              />
              <div
                className="my-1 mx-1 h-px bg-[var(--color-ink-08)]"
                aria-hidden
              />
              <MenuItem
                label="Log out"
                danger
                testId="profile-logout"
                onClick={() => {
                  onLogout?.();
                  setOpen(false);
                }}
                icon={
                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" aria-hidden>
                    <path
                      d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4M16 17l5-5-5-5M21 12H9"
                      stroke="currentColor"
                      strokeWidth="1.8"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                }
              />
            </div>
          </motion.div>
        ) : null}
      </AnimatePresence>
    </>
  );
}

/* ───────────────────────────────────────────────────────────
 * PlanBadge — Free/Lite/Pro use neutral chip; Enterprise gets gradient
 * ─────────────────────────────────────────────────────────── */

function PlanBadge({
  planName,
  isEnterprise,
}: {
  planName: string;
  isEnterprise: boolean;
}) {
  if (isEnterprise) {
    return (
      <span
        data-testid="profile-plan"
        className={[
          "inline-flex items-center h-5 px-2 mt-1.5 rounded-full",
          "text-[10px] font-semibold uppercase tracking-[0.14em]",
          "text-[var(--color-paper)]",
        ].join(" ")}
        style={{
          background:
            "linear-gradient(90deg, var(--color-brand-deep) 0%, #2d6f54 50%, #4ea088 100%)",
        }}
      >
        ⬢ Enterprise
      </span>
    );
  }
  return (
    <span
      data-testid="profile-plan"
      className={[
        "inline-flex items-center h-5 px-2 mt-1.5 rounded-full",
        "text-[10px] font-semibold uppercase tracking-[0.14em]",
        "bg-[var(--color-brand-soft)] text-[var(--color-brand-deep)]",
      ].join(" ")}
    >
      {planName}
    </span>
  );
}

/* ───────────────────────────────────────────────────────────
 * MenuItem — one row in the dropdown
 * ─────────────────────────────────────────────────────────── */

function MenuItem({
  label,
  icon,
  onClick,
  testId,
  kbd,
  danger = false,
}: {
  label: string;
  icon: React.ReactNode;
  onClick: () => void;
  testId: string;
  kbd?: string;
  danger?: boolean;
}) {
  return (
    <button
      type="button"
      role="menuitem"
      onClick={onClick}
      data-testid={testId}
      className={[
        "w-full inline-flex items-center gap-2",
        "px-2.5 py-1.5 rounded-[8px]",
        "text-[13px] font-medium text-left",
        "transition-colors duration-[var(--duration-fast)]",
        danger
          ? "text-[#9a3a3a] hover:bg-[rgb(214_64_64_/_0.08)]"
          : "text-[var(--color-ink)] hover:bg-[var(--color-ink-08)]",
      ].join(" ")}
    >
      <span
        className="shrink-0 inline-flex items-center justify-center w-[16px] h-[16px] text-[var(--color-ink-60)]"
        aria-hidden
      >
        {icon}
      </span>
      <span className="flex-1 truncate">{label}</span>
      {kbd ? (
        <span
          className={[
            "shrink-0 inline-flex items-center h-4 px-1 rounded-[4px]",
            "bg-[var(--color-ink-08)]/70 text-[10px] text-[var(--color-ink-40)]",
            "tabular-nums",
          ].join(" ")}
        >
          {kbd}
        </span>
      ) : null}
    </button>
  );
}

export default ProfileMenu;
